import React from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  children: ReactNode;
  ariaLabel?: string;
}

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled,
  children,
  className = '',
  ariaLabel,
  style,
  ...props
}) => {
  const base: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.4375rem',
    fontWeight: 600,
    fontFamily: 'inherit',
    borderRadius: 8,
    border: 'none',
    cursor: disabled || loading ? 'not-allowed' : 'pointer',
    opacity: disabled || loading ? 0.45 : 1,
    transition: 'all 0.18s ease',
    outline: 'none',
    whiteSpace: 'nowrap',
    letterSpacing: '-0.01em',
    pointerEvents: disabled || loading ? 'none' : 'auto',
  };

  const variants: Record<string, React.CSSProperties> = {
    primary: {
      background: '#3b82f6',
      color: '#fff',
    },
    secondary: {
      background: '#1a2235',
      color: '#cbd5e1',
      border: '1px solid #2a3a55',
    },
    outline: {
      background: 'transparent',
      color: '#94a3b8',
      border: '1px solid #1e2d45',
    },
    ghost: {
      background: 'transparent',
      color: '#64748b',
    },
    danger: {
      background: 'rgba(239,68,68,0.12)',
      color: '#f87171',
      border: '1px solid rgba(239,68,68,0.25)',
    },
  };

  const sizes: Record<string, React.CSSProperties> = {
    sm: { padding: '0.375rem 0.75rem', fontSize: '0.8125rem', minHeight: 32 },
    md: { padding: '0.5625rem 1.125rem', fontSize: '0.875rem', minHeight: 40 },
    lg: { padding: '0.75rem 1.5rem', fontSize: '0.9375rem', minHeight: 48 },
  };

  const hoverMap: Record<string, Partial<React.CSSProperties>> = {
    primary:   { background: '#2563eb', boxShadow: '0 4px 16px rgba(59,130,246,0.35)' },
    secondary: { background: '#1f2d44', borderColor: '#334155' },
    outline:   { background: 'rgba(255,255,255,0.05)', borderColor: '#2a3a55', color: '#cbd5e1' },
    ghost:     { background: 'rgba(255,255,255,0.05)', color: '#94a3b8' },
    danger:    { background: 'rgba(239,68,68,0.2)', borderColor: 'rgba(239,68,68,0.45)' },
  };

  const [hovered, setHovered] = React.useState(false);

  const combinedStyle: React.CSSProperties = {
    ...base,
    ...variants[variant],
    ...sizes[size],
    ...(hovered && !disabled && !loading ? hoverMap[variant] : {}),
    ...style,
  };

  return (
    <button
      style={combinedStyle}
      className={className}
      disabled={disabled || loading}
      aria-label={ariaLabel || (typeof children === 'string' ? children : undefined)}
      aria-busy={loading}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      {...props}
    >
      {loading && (
        <svg
          style={{ animation: 'spin 0.7s linear infinite', width: 14, height: 14, flexShrink: 0 }}
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity={0.25} />
          <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        </svg>
      )}
      {children}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </button>
  );
};
